import * as Twilio from "@twilio/voice-sdk"
import { useCallback, useRef, useState } from "react"
import { useChatToken } from "services/communications"

export function useTwilio({
	To,
	onHangUp,
}: {
	To?: string
	onHangUp: VoidFunction
}) {
	const device = useRef<Twilio.Device | null>(null)
	const call = useRef<Twilio.Call | null>(null)
	const { refetch } = useChatToken(false)
	const [muted, setMuted] = useState(false)
	const [deviceStatus, setDeviceStatus] = useState<Twilio.Device.State>(
		Twilio.Device.State.Unregistered,
	)
	const [callStatus, setCallStatus] = useState<Twilio.Call.State>(
		Twilio.Call.State.Closed,
	)
	const hangup = useCallback(async () => {
		device.current?.disconnectAll()
		await device.current?.unregister()
	}, [])
	const makeCall = useCallback(async () => {
		if (!To) {
			console.warn(
				`Unable to initiate call. The patient phone number seems empty: "${To}"`,
			)
			return null
		}

		if (!device.current) {
			console.warn(
				`Unable to initiate call. Twilio Device is not properly instantiated.`,
			)
			return null
		}

		console.debug(`Attempting to call ${To} ...`)

		// Clear any existing call first
		if (call.current) {
			call.current.disconnect()
			call.current = null
		}

		const twilioCall = await device.current.connect({ params: { To } })

		if (!twilioCall) {
			return null
		}

		call.current = twilioCall

		// add listeners to the Call
		// "accepted" means the call has finished connecting and the state is now "open"

		call.current.on("accept", () => {
			console.debug("Call.accept")
			setCallStatus(Twilio.Call.State.Open)
		})
		call.current.on("disconnect", async () => {
			console.debug("Call.disconnect")
			setCallStatus(Twilio.Call.State.Closed)
			await device.current?.unregister()
		})
		call.current.on("cancel", async () => {
			console.debug("Call.cancel")
			setCallStatus(Twilio.Call.State.Closed)
			await device.current?.unregister()
		})
		call.current.on("error", async () => {
			console.debug("Call.error")
			setCallStatus(Twilio.Call.State.Closed)
			await device.current?.unregister()
		})
		call.current.on("mute", (isMuted: boolean) => {
			console.debug("Call.mute", { isMuted })
			setMuted(isMuted)
		})
		call.current.on("reconnected", () => {
			console.debug("Call.reconnected")
			setCallStatus(Twilio.Call.State.Open)
		})
		call.current.on("reconnecting", () => {
			console.debug("Call.reconnecting")
			setCallStatus(Twilio.Call.State.Reconnecting)
		})
		call.current.on("reject", async (error) => {
			console.debug("Call.reject", error)
			setCallStatus(Twilio.Call.State.Closed)
			await device.current?.unregister()
		})
		call.current.on("ringing", () => {
			console.debug("Call.ringing")
			setCallStatus(Twilio.Call.State.Ringing)
		})

		return twilioCall
	}, [To])
	const intitializeDevice = useCallback(async () => {
		const { data: initialTokenRequest } = await refetch({
			throwOnError: true,
		})

		if (!initialTokenRequest?.token || device.current) {
			return
		}

		device.current = new Twilio.Device(initialTokenRequest.token, {
			logLevel: !!import.meta.env.PROD ? "error" : "info",
		})

		device.current.on(Twilio.Device.EventName.Destroyed, () => {
			console.debug(`Twilio.Device.EventName.Destroyed`)
			setDeviceStatus(Twilio.Device.State.Destroyed)
		})
		device.current.on(Twilio.Device.EventName.Error, () => {
			console.warn(`Twilio.Device.EventName.Error`)
		})
		device.current.on(Twilio.Device.EventName.Registered, async () => {
			console.debug(`Twilio.Device.EventName.Registered`)
			setDeviceStatus(Twilio.Device.State.Registered)

			const call = await makeCall()

			if (!call) {
				// just unwind current device if call fails to initiate
				await hangup()
			}
		})
		device.current.on(Twilio.Device.EventName.Registering, () => {
			console.debug(`Twilio.Device.EventName.Registering`)
			setDeviceStatus(Twilio.Device.State.Registering)
		})
		device.current.on(Twilio.Device.EventName.TokenWillExpire, async () => {
			console.debug(`Twilio.Device.EventName.TokenWillExpire`)

			console.debug(`Refetching Twilio token`)
			const { data: newTokenRequest } = await refetch({
				throwOnError: true,
			})

			if (!!newTokenRequest?.token && device.current) {
				console.debug(`Updating Twilio device with new token`)
				device.current.updateToken(newTokenRequest?.token)
			} else {
				console.warn(`Token refresh failed or no token available`)
			}
		})
		device.current.on(Twilio.Device.EventName.Unregistered, () => {
			console.debug(`Twilio.Device.EventName.Unregistered`)
			call.current?.disconnect()
			call.current = null
			device.current?.audio?.unsetInputDevice()
			device.current = null
			setDeviceStatus(Twilio.Device.State.Unregistered)
			onHangUp()
		})

		device.current.register()
	}, [hangup, makeCall, onHangUp, refetch])

	console.debug({ callStatus, deviceStatus, muted })

	return {
		deviceAvailable: deviceStatus === Twilio.Device.State.Registered,
		callOpen: callStatus === Twilio.Call.State.Open,
		muted,
		intitializeDevice,
		hangup,
		mute: () => {
			call.current?.mute(!muted)
		},
		sendDigit: (digit: string) => {
			call.current?.sendDigits(digit)
		},
		setInput: async (deviceId: string) => {
			await device.current?.audio?.setInputDevice(deviceId)
		},
		setOutput: async (deviceId: string) => {
			await device.current?.audio?.speakerDevices.set(deviceId)
		},
	}
}
